import React, { useContext, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import { addFavouriteRestaurant, profile as PROFILE_QUERY } from "../../apollo/server";
import UserContext from "../../context/User";
import { nextOpeningLabel, restaurantOpenStatus } from "../../utils/helper";
import { resizedImage, resizedSrcSet } from "../../utils/resizedImage";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY } from "../../utils/designTokens";

const ADD_FAVOURITE = gql`${addFavouriteRestaurant}`;
const PROFILE = gql`${PROFILE_QUERY}`;

export const chipBase = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  height: 28,
  px: 1.25,
  borderRadius: "999px",
  fontFamily: FONT_BODY,
  fontSize: 12,
  fontWeight: 700,
  whiteSpace: "nowrap",
  boxShadow: "0 1px 3px rgba(26,25,24,0.12)",
};

/** Minutes the card shows (prep time), also used by the filters and sort. */
export const prepMinutesOf = (r) => Number(r.defaultPrepTime) || 15;

/** "Open now" / "Closed · opens …" / "Opens soon · …" for the image's corner. */
export function statusOf(r) {
  const status = restaurantOpenStatus(r);
  if (status.status === "open" || status.status === "closing-soon") return { kind: "open", label: "Open now" };
  if (r.isMobile === true) return { kind: "closed", label: "Closed" };
  const opens = r.isAvailable === false ? null : nextOpeningLabel(r.openingTimes);
  if (status.status === "opening-soon") return { kind: "soon", label: opens ? `Opens soon · ${opens}` : "Opens soon" };
  return { kind: "closed", label: opens ? `Closed · opens ${opens}` : "Closed" };
}

export function StatusChip({ status }) {
  const look =
    status.kind === "open"
      ? { bg: C.greenSoft, color: C.green, dot: C.green }
      : status.kind === "soon"
        ? { bg: C.goldSoft, color: C.gold, dot: C.goldAccent }
        : { bg: C.bg, color: C.muted, dot: null };
  return (
    <Box component="span" sx={{ ...chipBase, bgcolor: look.bg, color: look.color }}>
      {look.dot && <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: look.dot }} />}
      {status.label}
    </Box>
  );
}

export function Rating({ r }) {
  const count = r.reviewData?.total || r.reviewData?.reviews?.length || 0;
  const avg = Number(r.reviewData?.ratings) || 0;
  if (!count) {
    return (
      <Box component="span" sx={{ flexShrink: 0, px: 1, height: 22, display: "inline-flex", alignItems: "center", borderRadius: "999px", bgcolor: C.greenSoft, color: C.green, fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700 }}>
        New
      </Box>
    );
  }
  return (
    <Box component="span" sx={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 0.25, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: C.ink }} aria-label={`Rated ${avg.toFixed(1)} from ${count} reviews`}>
      <StarRoundedIcon aria-hidden="true" sx={{ fontSize: 17, color: C.goldAccent }} />
      {avg.toFixed(1)}
      <Box component="span" sx={{ color: C.muted, fontWeight: 500 }}>({count})</Box>
    </Box>
  );
}

/**
 * One restaurant on the list. The name is the card's link (stretched over the
 * whole card); the heart sits above it. `onOpen` opens the restaurant (the
 * list checks the cart first), `dealCount` is its live deals, `onMessage`
 * shows the page's snackbar.
 */
function RestaurantListCard({ r, dealCount = 0, onOpen, onMessage }) {
  const { profile } = useContext(UserContext);
  const saved = Boolean(profile?.favourite?.includes(r._id));
  const [fav, setFav] = useState(saved);
  useEffect(() => setFav(saved), [saved]);
  const [toggleFavourite] = useMutation(ADD_FAVOURITE, {
    refetchQueries: [{ query: PROFILE }],
    onError: () => {
      setFav(saved);
      onMessage?.({ type: "error", message: "Couldn't update your favourites. Please try again." });
    },
  });

  const status = statusOf(r);
  const closed = status.kind !== "open";
  const miles = r.distance != null ? (r.distance * 0.621371).toFixed(1) : null;
  const truckLine = r.isMobile === true ? r.openStatusLabel || r.serviceLocation?.nextStopLabel || null : null;
  // The banner photo; the logo sits on it as a small icon. A restaurant with
  // only a logo shows that instead.
  const image = r.image || r.logo;
  const logo = r.image && r.logo && r.logo !== r.image ? r.logo : null;

  const onHeart = () => {
    if (!profile) {
      onMessage?.({ type: "error", message: "Please log in to add favorites" });
      return;
    }
    setFav((v) => !v);
    toggleFavourite({ variables: { id: r._id } });
  };

  return (
    <Box
      component="article"
      sx={{
        position: "relative",
        minWidth: 0,
        "&:hover .rl-img": { transform: "scale(1.03)" },
        "&:focus-within .rl-name": { textDecoration: "underline" },
      }}
    >
      <Box sx={{ position: "relative", aspectRatio: "16 / 10", borderRadius: "18px", overflow: "hidden", bgcolor: C.surface2 }}>
        {image && (
          <Box
            component="img"
            className="rl-img"
            src={resizedImage(image, 800)}
            srcSet={resizedSrcSet(image, [480, 800])}
            sizes="(max-width: 600px) 100vw, 360px"
            alt=""
            loading="lazy"
            decoding="async"
            sx={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "cover",
              filter: closed ? "grayscale(35%) brightness(0.92)" : "none",
              transition: "transform .3s ease",
            }}
          />
        )}
        <Box sx={{ position: "absolute", top: 12, left: 12, right: 64, display: "flex", gap: 0.75, flexWrap: "wrap", pointerEvents: "none" }}>
          <StatusChip status={status} />
          {r.isMobile === true && (
            <Box component="span" sx={{ ...chipBase, bgcolor: C.bg, color: C.ink }}>
              <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
              Food truck
            </Box>
          )}
          {r.hasCatering && (
            <Box component="span" sx={{ ...chipBase, bgcolor: C.redTint, color: C.red }}>
              <RoomServiceRoundedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
              Catering
            </Box>
          )}
        </Box>
        {logo && (
          <Box
            component="img"
            src={resizedImage(logo, 128)}
            alt=""
            loading="lazy"
            decoding="async"
            sx={{
              position: "absolute",
              right: 12,
              bottom: 12,
              width: 44,
              height: 44,
              boxSizing: "border-box",
              borderRadius: "12px",
              border: "2px solid #FFFFFF",
              bgcolor: C.bg,
              objectFit: "cover",
              boxShadow: "0 2px 8px rgba(26,25,24,0.20)",
              pointerEvents: "none",
            }}
          />
        )}
        {dealCount > 0 && (
          <Box component="span" sx={{ ...chipBase, position: "absolute", left: 12, bottom: 12, bgcolor: C.red, color: "#FFFFFF", pointerEvents: "none" }}>
            <LocalOfferRoundedIcon aria-hidden="true" sx={{ fontSize: 15 }} />
            {dealCount} {dealCount === 1 ? "deal" : "deals"}
          </Box>
        )}
      </Box>

      {/* Above the card's stretched link. */}
      <Box
        component="button"
        type="button"
        onClick={onHeart}
        aria-pressed={fav}
        aria-label={fav ? `Remove ${r.name} from favourites` : `Save ${r.name} to favourites`}
        sx={{
          ...BUTTON_RESET,
          ...FOCUS_RING,
          position: "absolute",
          top: 10,
          right: 10,
          zIndex: 1,
          width: 40,
          height: 40,
          borderRadius: "50%",
          bgcolor: C.bg,
          color: fav ? C.red : C.ink,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 2px 8px rgba(26,25,24,0.16)",
          transition: "transform .15s ease",
          "&:hover": { transform: "scale(1.06)" },
          "&:active": { transform: "scale(0.94)" },
        }}
      >
        {fav ? <FavoriteRoundedIcon aria-hidden="true" sx={{ fontSize: 22 }} /> : <FavoriteBorderRoundedIcon aria-hidden="true" sx={{ fontSize: 22 }} />}
      </Box>

      <Box sx={{ pt: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="button"
            type="button"
            className="rl-name"
            onClick={() => onOpen(r)}
            sx={{
              ...BUTTON_RESET,
              flex: 1,
              minWidth: 0,
              textAlign: "left",
              fontFamily: FONT_BODY,
              fontSize: 17,
              fontWeight: 700,
              lineHeight: 1.3,
              color: C.ink,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              // Stretched over the card: the whole card opens the restaurant.
              "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "18px" },
              "&:focus-visible": { outline: "none" },
              "&:focus-visible::after": { outline: `2px solid ${C.ink}`, outlineOffset: "4px" },
            }}
          >
            {r.name}
          </Box>
          <Rating r={r} />
        </Box>
        {(r.cuisines || []).length > 0 && (
          <Box sx={{ fontFamily: FONT_BODY, fontSize: 14, color: C.muted, mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {r.cuisines.join(" · ")}
          </Box>
        )}
        {truckLine && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: closed ? C.muted : C.green }}>
            <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
            <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{truckLine}</Box>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75, fontFamily: FONT_BODY, fontSize: 13, color: C.muted, whiteSpace: "nowrap" }}>
          {miles !== null && (
            <>
              <span>{miles} mi</span>
              <span aria-hidden="true">•</span>
            </>
          )}
          <span>{prepMinutesOf(r)} min</span>
          <span aria-hidden="true">•</span>
          <Box component="span" sx={{ color: C.redLink, fontWeight: 700 }}>
            {closed ? "Schedule order" : "Order now"}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(RestaurantListCard);
